import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  FileText, 
  MapPin,
  X,
  ChevronLeft,
  ChevronRight,
  Wind,
  Thermometer,
  Snowflake,
  Fan
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const RealisationsPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'Toutes', icon: null },
    { id: 'climatisation', label: 'Climatisation', icon: Wind },
    { id: 'pac', label: 'Pompe à chaleur', icon: Thermometer },
    { id: 'froid', label: 'Froid commercial', icon: Snowflake },
    { id: 'ventilation', label: 'Ventilation', icon: Fan }
  ];

  const realisations = [
    // Photos réelles ADM Confort Clim - Lot 1
    {
      id: 1,
      category: 'climatisation',
      title: "Climatisation cassette restaurant",
      location: "Seine-et-Marne (77)",
      description: "Installation d'une climatisation cassette design avec éclairage intégré dans un restaurant. Système performant et esthétique pour les espaces commerciaux.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/s5eba2in_installation-climatisation-restaurant-plafond-commercial-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      id: 2,
      category: 'froid',
      title: "Climatisation cassette supermarché",
      location: "Seine-et-Marne (77)",
      description: "Installation de climatisation cassette plafond dans un commerce alimentaire. Solution professionnelle pour maintenir une température optimale.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/qv5ds9gy_installation-climatisation-cassette-commerce-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      id: 3,
      category: 'ventilation',
      title: "Réseau aéraulique professionnel",
      location: "Seine-et-Marne (77)",
      description: "Installation complète d'un réseau de gaines de ventilation avec diffuseurs circulaires dans des locaux professionnels.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/bod0pe3z_installation-reseau-aeraulique-gaine-ventilation-locaux-professionnels-seine-et-marne.jpg"
    },
    {
      id: 4,
      category: 'ventilation',
      title: "Réseau aéraulique extérieur restaurant",
      location: "Restaurant Lucrezia (77)",
      description: "Installation d'un imposant réseau de gaines aérauliques en façade extérieure pour un restaurant. Travail technique de grande envergure.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/s7p1z51y_installation-reseau-aeraulique-exterieur-restaurant-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      id: 5,
      category: 'climatisation',
      title: "Installation cassette Mitsubishi",
      location: "Seine-et-Marne (77)",
      description: "Pose d'une climatisation cassette Mitsubishi Electric en cours de chantier. Installation soignée avec fixation au plafond.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/y6zg9zb5_installation-reseau-aeraulique-moteur-extraction-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    // Photos réelles ADM Confort Clim - Lot 2
    {
      id: 6,
      category: 'climatisation',
      title: "Climatisation cassette magasin Monceau Fleurs",
      location: "Seine-et-Marne (77)",
      description: "Installation de 2 cassettes de climatisation plafond dans un magasin Monceau Fleurs. Chantier en cours avec finitions soignées.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/e8t5pvjv_installation-climatisation-cassette-magasin-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      id: 7,
      category: 'climatisation',
      title: "Climatisation gainable boutique décoration",
      location: "Seine-et-Marne (77)",
      description: "Installation d'une climatisation gainable avec grille de soufflage design dans une boutique de décoration. Intégration discrète et élégante.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/t0krh7zc_installation-grille-soufflage-climatisation-commerce-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      id: 8,
      category: 'pac',
      title: "Plancher chauffant électrique maison",
      location: "Seine-et-Marne (77)",
      description: "Installation de plancher chauffant électrique dans une maison individuelle. Solution de chauffage performante et économique.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/j1mldqe1_installation-plancher-chauffant-electrique-maison-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    // Photos réelles ADM Confort Clim - Lot 3
    {
      id: 9,
      category: 'climatisation',
      title: "Télécommandes Mitsubishi Heavy Industries",
      location: "Seine-et-Marne (77)",
      description: "Installation de commandes murales Mitsubishi Heavy Industries pour climatisation. Contrôle précis de la température avec écran LCD.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/huhp56ng_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 10,
      category: 'climatisation',
      title: "Installation climatisation professionnelle",
      location: "Seine-et-Marne (77)",
      description: "Réalisation d'une installation de climatisation professionnelle en Seine-et-Marne. Travail soigné et matériel de qualité.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/nrd6bq10_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 11,
      category: 'climatisation',
      title: "Intervention climatisation commerce",
      location: "Seine-et-Marne (77)",
      description: "Intervention sur système de climatisation dans un local commercial. Expertise et réactivité pour les professionnels.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/n7w3njrr_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 12,
      category: 'pac',
      title: "Installation pompe à chaleur",
      location: "Seine-et-Marne (77)",
      description: "Installation d'une pompe à chaleur air/eau en Seine-et-Marne. Solution économique et écologique pour le chauffage.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/ibxtuu6y_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 13,
      category: 'ventilation',
      title: "Installation ventilation professionnelle",
      location: "Seine-et-Marne (77)",
      description: "Mise en place d'un système de ventilation pour locaux professionnels. Qualité d'air optimale garantie.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/lo10s7di_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    // Photos réelles ADM Confort Clim - Lot 4
    {
      id: 14,
      category: 'climatisation',
      title: "Mise en service unité extérieure climatisation",
      location: "Seine-et-Marne (77)",
      description: "Mise en service d'une unité extérieure de climatisation sur terrasse bois. Utilisation de pompe à vide professionnelle pour une installation aux normes.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/6nf5q34b_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 15,
      category: 'climatisation',
      title: "Installation unité extérieure maison",
      location: "Seine-et-Marne (77)",
      description: "Pose d'une unité extérieure de climatisation sur maison individuelle. Fixation soignée et raccordements professionnels.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/5mmbcta0_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 16,
      category: 'pac',
      title: "Installation groupe extérieur PAC",
      location: "Seine-et-Marne (77)",
      description: "Installation d'un groupe extérieur de pompe à chaleur. Solution performante pour le chauffage et la climatisation.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/utf3g08y_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 17,
      category: 'climatisation',
      title: "Raccordement frigorifique climatisation",
      location: "Seine-et-Marne (77)",
      description: "Raccordement frigorifique professionnel sur installation de climatisation. Travail technique réalisé dans les règles de l'art.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/m4u3cjdi_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 18,
      category: 'climatisation',
      title: "Intervention technique climatisation",
      location: "Seine-et-Marne (77)",
      description: "Intervention technique sur système de climatisation en Seine-et-Marne. Équipement professionnel et expertise certifiée.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/jgbghqme_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    // Photos réelles ADM Confort Clim - Lot 5
    {
      id: 19,
      category: 'climatisation',
      title: "Unités extérieures avec cache décoratif DecoClim",
      location: "Seine-et-Marne (77)",
      description: "Installation de 2 unités extérieures de climatisation avec caches décoratifs DecoClim blancs. Finition premium et esthétique soignée.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/dspg48bw_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 20,
      category: 'climatisation',
      title: "Installation climatisation résidentielle premium",
      location: "Seine-et-Marne (77)",
      description: "Installation complète de climatisation dans une résidence avec finitions haut de gamme. Travail soigné et esthétique.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/9fhmoux5_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 21,
      category: 'pac',
      title: "Groupe extérieur pompe à chaleur maison",
      location: "Seine-et-Marne (77)",
      description: "Installation d'un groupe extérieur de pompe à chaleur sur maison individuelle. Solution économique et écologique.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/101m5wip_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 22,
      category: 'climatisation',
      title: "Installation split mural climatisation",
      location: "Seine-et-Marne (77)",
      description: "Pose d'un climatiseur split mural dans une pièce de vie. Installation propre et discrète pour un confort optimal.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/fco061pk_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      id: 23,
      category: 'climatisation',
      title: "Finition installation climatisation professionnelle",
      location: "Seine-et-Marne (77)",
      description: "Finition soignée d'une installation de climatisation professionnelle. Travail de qualité avec attention aux détails.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/mqux4oem_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    // Photos réelles ADM Confort Clim - Lot 6 (dernier lot)
    {
      id: 24,
      category: 'pac',
      title: "Pompe à chaleur avec cache décoratif jardin",
      location: "Seine-et-Marne (77)",
      description: "Installation de 2 groupes extérieurs de pompe à chaleur avec caches décoratifs blancs dans un jardin paysager. Intégration esthétique parfaite.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/aoe4xejv_WhatsApp%20Image%202026-03-15%20at%2022.05.58.jpeg"
    },
    {
      id: 25,
      category: 'pac',
      title: "Installation unité extérieure Toshiba",
      location: "Seine-et-Marne (77)",
      description: "Installation d'une unité extérieure Toshiba avec goulottes. Raccordement soigné et installation aux normes.",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/qfes4pd6_WhatsApp%20Image%202026-03-15%20at%2022.05.58.jpeg"
    }
  ];

  const filteredRealisations = activeFilter === 'all' 
    ? realisations 
    : realisations.filter(r => r.category === activeFilter);

  const openLightbox = (realisation) => {
    setSelectedImage(realisation);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateLightbox = (direction) => {
    const currentIndex = filteredRealisations.findIndex(r => r.id === selectedImage.id);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredRealisations.length - 1;
    } else {
      newIndex = currentIndex < filteredRealisations.length - 1 ? currentIndex + 1 : 0;
    }
    setSelectedImage(filteredRealisations[newIndex]);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[#F8FAFC] py-20" data-testid="realisations-hero">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="badge-primary mb-4">Portfolio</span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-[#000000] mt-4 mb-6">
              Nos réalisations
            </h1>
            <p className="text-slate-600 text-lg">
              Découvrez quelques-unes de nos installations réalisées en Île-de-France et dans toute la France. 
              Chaque projet est unique et réalisé avec le plus grand soin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-100 sticky top-24 bg-white z-30" data-testid="realisations-filter">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeFilter === cat.id
                    ? 'bg-[#1997b0] text-white'
                    : 'bg-[#F8FAFC] text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.icon && <cat.icon size={16} />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-padding bg-white" data-testid="realisations-gallery">
        <div className="container-custom">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            layout
          >
            {filteredRealisations.map((realisation, index) => (
              <motion.div
                key={realisation.id}
                className="gallery-item aspect-[4/3] cursor-pointer"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                onClick={() => openLightbox(realisation)}
                data-testid={`realisation-${realisation.id}`}
                layout
              >
                <img 
                  src={realisation.image}
                  alt={realisation.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <h3 className="font-outfit font-semibold text-lg">{realisation.title}</h3>
                  <p className="text-white/80 text-sm flex items-center gap-1">
                    <MapPin size={14} />
                    {realisation.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredRealisations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">Aucune réalisation dans cette catégorie pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-4xl p-0 bg-white overflow-hidden">
          {selectedImage && (
            <div className="relative">
              <img 
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain bg-slate-100"
              />
              
              {/* Navigation */}
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight size={24} />
              </button>
              
              {/* Info */}
              <div className="p-6">
                <h3 className="font-outfit text-xl font-semibold text-[#000000] mb-1">
                  {selectedImage.title}
                </h3>
                <p className="text-slate-500 flex items-center gap-1 text-sm mb-3">
                  <MapPin size={14} />
                  {selectedImage.location}
                </p>
                <p className="text-slate-600">{selectedImage.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info section */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="realisations-info">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#000000] mb-4">
              13 ans d'expertise à votre service
            </h2>
            <p className="text-slate-600 mb-8">
              Chaque projet est réalisé avec le plus grand soin. Nos techniciens qualifiés interviennent 
              sur tous types d'installations : climatisation, ventilation, froid commercial. 
              Contactez-nous pour découvrir toutes nos références.
            </p>
            <Link 
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="realisations-contact-cta"
            >
              <FileText size={18} />
              <span>Demander un devis gratuit</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1997b0]" data-testid="realisations-cta">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Vous avez un projet similaire ?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Contactez-nous pour discuter de votre projet et obtenir un devis gratuit personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:0603054911"
                className="btn-emergency flex items-center gap-2"
                data-testid="realisations-cta-call"
              >
                <Phone size={20} />
                <span>06 03 05 49 11</span>
              </a>
              <Link 
                to="/contact"
                className="bg-white text-[#1997b0] hover:bg-slate-100 transition-all duration-300 rounded-full px-8 py-4 font-semibold flex items-center gap-2"
                data-testid="realisations-cta-contact"
              >
                <FileText size={20} />
                <span>Demander un devis gratuit</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealisationsPage;
